import { Box, Flex, Heading, useDisclosure } from '@chakra-ui/react';
import { BaseButton, BaseDialog, BaseEmptyState, BaseHelpText, BaseWidget } from '@cks-internal/react-base';
import { faRefresh, faSailboat, faUsers, faUserXmark, faWrench } from '@fortawesome/free-solid-svg-icons';
import { useMemo, useState } from 'react';
import { SetScheduleForm } from '../../features/workflow/forms/ScheduleForm';
import { StatusForm } from '../../features/workflow/forms/StatusForm';
import { ManagedUser } from '../../features/workflow/ManagedUser';
import { ScheduleTable } from '../../features/workflow/ScheduleTable';
import { UserType } from '../../features/workflow/types';
import { useWorkflowSocket } from '../../features/workflow/hub/useWorkflowSocket';
import { sortUsers } from '../../features/workflow/utils/sortUsers';
import { ModeButtons } from '../../features/workflow/ModeButtons';
import { QuickAccessUser } from '../../features/workflow/QuickAccessUser';
import { useWidgetStore } from '../../features/workflow/widgetStore';

export enum wfWigitModes {
  Lock,
  Increment,
  Decrement
}

export const CoordinatorWidget = () => {
  const users = useWorkflowSocket((r) => r.data?.users);
  const invoke = useWorkflowSocket((r) => r.invoke);
  const scheduleDialog = useDisclosure();
  const [targetUser, setTargetUser] = useState<UserType | undefined>();
  const [mode, setMode] = useState<wfWigitModes>(wfWigitModes.Lock);
  const store = useWidgetStore();
  const userQuickAccess = useDisclosure({
    onClose() {
      setTargetUser(undefined);
    }
  });
  const setScheduleForm = useDisclosure({
    onClose() {
      if (!userQuickAccess.open) {
        setTargetUser(undefined);
      }
    }
  });
  const setStatusFrom = useDisclosure({
    onClose() {
      if (!userQuickAccess.open) {
        setTargetUser(undefined);
      }
    }
  });
  const isEmpty = useMemo(() => Object.entries(users ?? {}).length == 0, [users]);
  const count = useMemo(() => Object.entries(users ?? {}).length, [users]);
  if (store.docked) {
    return <></>;
  }
  return (
    <>
      <BaseWidget
        keepStateOnClose
        settingsIcon={faWrench}
        controlProps={
          store.dockedMode
            ? {
                onMinMax() {
                  store.toggleDock();
                }
              }
            : undefined
        }
        settingsItems={[
          {
            icon: faSailboat,
            text: 'Toggle Dock Mode',
            onClick() {
              store.toggleDockMode();
              if (!store.dockedMode) {
                store.toggleDock();
              }
            }
          },
          {
            icon: faRefresh,
            text: 'Refresh Widget',
            onClick() {
              invoke('RefreshUsers');
            }
          },
          {
            icon: faUsers,
            text: 'Schedules',
            onClick() {
              scheduleDialog.onOpen();
            }
          },
          {
            icon: faUserXmark,
            text: 'Leave',
            onClick() {
              invoke('Leave', false);
            }
          }
        ]}
        minW={0}
        minH={0}
        positioner={
          store.dockedMode
            ? { horizontal: 'center', vertical: 'top' }
            : {
                horizontal: 'left',
                vertical: 'bottom'
              }
        }
        styleHeader={{
          minH: '20px',
          p: '4px',
          m: '0px',
          fontSize: 'sm',
          justifyContent: 'center',
          ml: '2rem',
          mr: '2rem'
        }}
        styleContent={{
          border: '2px solid black',
          borderRadius: '12px',
          minW: 'fit-content',
          minH: 'fit-content'
        }}
        styleBody={{ display: 'flex', flexDir: 'column', justifyContent: 'center', width: '100%', p: '0px', m: '0px' }}
        header={<Box textAlign={'center'}>Workflow Users — {count} Online</Box>}
        wrapper={({ children }) => (
          <Flex flexDir={'column'} alignItems={'center'} justifyContent={'center'} gap={'12px'} children={children} />
        )}>
        {isEmpty && <BaseEmptyState size={'sm'} title='No one else is on shift' />}
        {!isEmpty && (
          <>
            <BaseButton size={'xs'} toolTip={'click this if users are missing'} icon={faRefresh}>
              Refresh Widget
            </BaseButton>
            <ModeButtons setMode={setMode} mode={mode} />
            {Object.entries({ ...users })
              .sort(([_a, aValue], [_b, bValue]) => sortUsers(aValue, bValue))
              .map(([key, value]) => (
                <ManagedUser
                  mode={mode}
                  onUserDialog={(user, type) => {
                    setTargetUser(user);
                    if (type == 'status') {
                      setStatusFrom.onOpen();
                    }
                    if (type == 'schedules') {
                      setScheduleForm.onOpen();
                    }
                  }}
                  key={key}
                  user={value}
                />
              ))}
          </>
        )}
      </BaseWidget>

      <BaseDialog
        header={`Quick Access ${targetUser?.Name ?? ''} `}
        disclosure={userQuickAccess}
        dragDialog={{
          lockSize: true,
          minH: 0,
          minW: 0
        }}>
        <ModeButtons setMode={setMode} mode={mode} />
        {targetUser?.Id != undefined && (
          <QuickAccessUser
            onUserDialog={(user, type) => {
              setTargetUser(user);
              if (type == 'status') {
                setStatusFrom.onOpen();
              }
              if (type == 'schedules') {
                setScheduleForm.onOpen();
              }
            }}
            mode={mode}
            id={targetUser.Id}
          />
        )}
      </BaseDialog>
      <BaseDialog
        header={
          <Heading>
            Schedules <BaseHelpText text={'Sorting shows the next upcoming time'} />
          </Heading>
        }
        disclosure={scheduleDialog}
        dragDialog={{ initialW: 900, initialH: 560, minW: 600, minH: 360, styleBody: { overflow: 'hidden' } }}>
        <ScheduleTable />
      </BaseDialog>
      {targetUser?.Id && targetUser.Name && (
        <>
          <SetScheduleForm
            shift={targetUser.Shift}
            lunch={targetUser.Lunch}
            disclosure={setScheduleForm}
            id={targetUser.Id}
            userName={targetUser.Name}
          />
          <StatusForm
            isDraggable
            disclosure={setStatusFrom}
            userId={targetUser.Id}
            userName={targetUser.Name}
            trigger={<Box position='absolute' inset={0} pointerEvents='none' />}
          />
        </>
      )}
    </>
  );
};
